"use client";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { submitLead } from "../../../app/utilities/apis";
import { sanitizePhoneInput, validateEmail, validateName, validatePhoneNumber } from "../../../app/utilities/formValidation";
import { LeadFormData } from "../../../app/utilities/interfaces";
import Notification from "../../Notification";
import CustomInput from "../CustomInput";
import CustomSelect from "../CustomSelect";
import ImageWrapper from "../ImageWrapper";
import LocationMasterSearchInput from "../LocationMasterSearchInput";
import { mapLocationToLeadFields } from "@/app/utilities/locationUtils";
import LeadOtpStep from "./LeadOtpStep";
import LeadDealerStep from "./LeadDealerStep";
import { useLeadFormConfig } from "@/utils/hooks/useLeadFormConfig";
import ThanksPopup from '@/component/ThanksPopup';
import { acquireModalSession, releaseModalSession } from "@/lib/client/modalSession";
import { THANKS_POPUP_SESSION_ID } from "@/app/utilities/constants";
import { consumeMobileLeadKeyboardRequest, focusFirstEmptyLeadFieldOnMobile } from "./LeadForm";
import { useLeadConsent } from "@/utils/hooks/useLeadConsent";
import LeadConsent from "./LeadConsent";

// Only the fields this form's model dropdown actually renders — the full
// Model object (images, spec/feature sections, descriptions, etc.) would
// otherwise get serialized into the RSC payload for every model on the brand.
export interface ModelOption {
    _id: string;
    brand_name: string;
    name: string;
}

interface Props {
    showModal: boolean;
    models: ModelOption[];
    brandName?: string;
    onClose: () => void;
    onSubmit: () => void;
    onError: (msg: string) => void;
}

const BrandIntentLeadForm: React.FC<Props> = ({
    showModal,
    models,
    brandName,
    onClose,
    onSubmit,
    onError,
}) => {
    const [lead, setLead] = useState<LeadFormData>({ name: "", phone: "", location: "" });
    const [selectedModel, setSelectedModel] = useState("");
    const [errors, setErrors] = useState<Record<string, string | null>>({});
    const [isLoading, setIsLoading] = useState(false);
    // Mounted while closed — fetch the consent config only once opened.
    const consent = useLeadConsent(showModal);
    const [otpState, setOtpState] = useState<{ leadId: string; phone: string } | null>(null);
    // Email field appears only once a model with email_required is selected
    const { emailRequired, dealerSelectionEnabled, loading: leadConfigLoading } = useLeadFormConfig(selectedModel || null);
    // Set once a lead is confirmed on a dealer-selection model; shows the dealer step
    const [dealerLeadId, setDealerLeadId] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ msg: string | null; error: boolean; show: boolean }>({
        msg: null,
        error: false,
        show: false,
    });

    const prevShowRef = useRef(showModal);
    const lastLeadPayloadRef = useRef<Record<string, unknown> | null>(null);
    const [showThanks, setShowThanks] = useState(false);
    const [lastLeadData, setLastLeadData] = useState<any>(null);
    const [thanksModel, setThanksModel] = useState<any>(null);
    const [thanksModelId, setThanksModelId] = useState<string | null>(null);

    // Reset form when modal closes
    useEffect(() => {
        if (prevShowRef.current && !showModal) {
            setLead({ name: "", phone: "", location: "" });
            setSelectedModel("");
            setErrors({});
            setOtpState(null);
            setDealerLeadId(null);
        }
        prevShowRef.current = showModal;
    }, [showModal]);

    // Pre-fill from cookies when modal opens
    useEffect(() => {
        if (!showModal) return;

        let name = "", phone = "";

        // User cookie
        const userMatch = document.cookie.match(/(?:^|; )user=([^;]*)/);
        if (userMatch?.[1]) {
            try {
                const user = JSON.parse(decodeURIComponent(userMatch[1]));
                name = user.user?.name || "";
                phone = user.user?.mobile || "";
            } catch {
                // silent fail
            }
        }

        setLead({ name, phone, location: "", locationData: {} });
    }, [showModal]);

    const showToast = (msg: string, isError = false) => {
        setNotification({ msg, error: isError, show: true });
        setTimeout(() => setNotification((p) => ({ ...p, show: false })), 3200);
    };

    const validateForm = () => {
        const newErrors: Record<string, string | null> = {};
        const t = {
            name: lead.name.trim(),
            phone: lead.phone.trim(),
            location: (lead.location || "").trim(),
        };

        if (!t.name) newErrors.name = "Name is required";
        if (!t.phone) newErrors.phone = "Mobile number is required";
        else {
            const phoneErr = validatePhoneNumber(t.phone);
            if (phoneErr) newErrors.phone = phoneErr;
        }
        if (!t.location) newErrors.location = "Location is required";
        if (emailRequired) {
            const emailErr = validateEmail(lead.email || "");
            if (emailErr) newErrors.email = emailErr;
        }

        if (!selectedModel) newErrors.model = "Please select a model";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const closeThanksPopup = () => {
        setShowThanks(false);
        releaseModalSession(THANKS_POPUP_SESSION_ID);
    };

    // Shared success path for both the no-OTP flow and the OTP-verified flow
    const finalizeSuccess = () => {
        const selectedModelData = models.find((m) => m._id === selectedModel);
        const payload = lastLeadPayloadRef.current ?? {};
        const leadData = {
            ...payload,
            brand: (payload as { brand?: string }).brand ?? selectedModelData?.brand_name ?? brandName,
            modelName: (payload as { modelName?: string }).modelName ?? selectedModelData?.name,
        };
        if (typeof window !== "undefined") {
            (window as any).__LAST_LEAD_DATA__ = leadData;
            if (selectedModel) {
                window.dispatchEvent(
                    new CustomEvent("lead-submitted", {
                        detail: {
                            carId: selectedModel,
                            carName: `${selectedModelData?.brand_name ?? ""} ${selectedModelData?.name ?? ""}`.trim(),
                        },
                    }),
                );
            }
        }
        setLastLeadData(leadData);
        setThanksModelId(selectedModel || null);
        setThanksModel(selectedModelData ?? null);
        acquireModalSession(THANKS_POPUP_SESSION_ID);
        setShowThanks(true);
        onSubmit(); // Hide modal immediately
        setLead({ name: "", phone: "", location: "", locationData: {} });
        setSelectedModel("");
        setErrors({});
        setOtpState(null);
        setDealerLeadId(null);
    };

    // Runs once a lead is confirmed — routes through the dealer step first for
    // dealer-selection models.
    const handleLeadConfirmed = (leadId?: string) => {
        if (dealerSelectionEnabled && leadId) {
            setOtpState(null);
            setDealerLeadId(leadId);
            return;
        }
        finalizeSuccess();
    };

    const handleSubmit = async () => {
        if (isLoading || !consent.canSubmit) return; // guard against rapid double-clicks

        setIsLoading(true);

        if (!validateForm()) {
            showToast("Please fill all required fields correctly", true);
            onError("Validation failed");
            setIsLoading(false);
            return;
        }

        try {
            const { buildLeadPayload } = await import('@/app/utilities/locationUtils');
            // Never send email for a model that doesn't collect it
            const { email, ...rest } = lead;
            const payload = buildLeadPayload(emailRequired ? { ...rest, email } : rest, { carId: selectedModel });
            lastLeadPayloadRef.current = payload;
            const res = await submitLead(payload);

            if (res?.status === "otp_required") {
                setOtpState({ leadId: res.data.lead_id, phone: res.data.phone });
                return;
            }

            if (res?.status !== "success") throw new Error("Submission failed");

            handleLeadConfirmed(res.data?._id);
        } catch (err) {
            console.error("Lead submission error:", err);
            const msg = "Something went wrong. Please try again.";
            showToast(msg, true);
            onError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const showLeadFields = showModal && !dealerLeadId && !otpState;
    const leadFormAutofocusRef = useRef<HTMLDivElement>(null);
    const didAutofocusRef = useRef(false);

    useLayoutEffect(() => {
        if (!showLeadFields) {
            didAutofocusRef.current = false;
            return;
        }
        if (didAutofocusRef.current) return;
        didAutofocusRef.current = true;
        const fromUserGesture = consumeMobileLeadKeyboardRequest();
        const runFocus = () => focusFirstEmptyLeadFieldOnMobile(leadFormAutofocusRef.current);
        if (fromUserGesture) {
            runFocus();
            requestAnimationFrame(runFocus);
            return;
        }
        const timer = window.setTimeout(runFocus, 220);
        return () => window.clearTimeout(timer);
    }, [showLeadFields]);

    if (!showModal && !showThanks) {
        return (
            <Notification
                message={notification.msg}
                isVisible={notification.show}
                isError={notification.error}
            />
        );
    }

    const isDisabled = isLoading || leadConfigLoading || !consent.canSubmit
    const selectedModelData = models.find((m) => m._id === selectedModel);

    return ReactDOM.createPortal(
        <>
            {showModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]">
                <div className="bg-white rounded-3xl w-[328px] md:w-[448px] max-w-full overflow-hidden">
                    <div className="p-5 md:p-6 relative" ref={leadFormAutofocusRef}>
                        <button
                            onClick={dealerLeadId ? finalizeSuccess : onClose}
                            className="absolute top-5 right-6 z-10"
                            aria-label="Close"
                        >
                            <ImageWrapper
                                src="/popup-close.svg"
                                alt="close"
                                width={18}
                                height={18}
                                cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                            />
                        </button>

                        <h2 className="text-xl md:text-2xl font-medium text-gray-800 mb-6 pr-10">
                            Get On Road Price for <span className="text-teal-600" >{brandName}</span>
                        </h2>

                        <div className="space-y-4">
                            {dealerLeadId ? (
                                <LeadDealerStep
                                    leadId={dealerLeadId}
                                    carName={selectedModelData ? `${selectedModelData.brand_name} ${selectedModelData.name}` : undefined}
                                    onDone={finalizeSuccess}
                                />
                            ) : otpState ? (
                                <LeadOtpStep
                                    leadId={otpState.leadId}
                                    phone={otpState.phone}
                                    onVerified={() => handleLeadConfirmed(otpState.leadId)}
                                    onCancel={() => setOtpState(null)}
                                />
                            ) : (
                            <>
                            <CustomSelect
                                options={models.map((m) => ({
                                    value: m._id,
                                    label: `${m.brand_name} ${m.name}`,
                                }))}
                                value={selectedModel}
                                onChange={(v) => {
                                    setSelectedModel(v);
                                    setErrors((p) => ({ ...p, model: null }));
                                }}
                                placeholder={`Select ${brandName} model`}
                                leftIcon={
                                    <ImageWrapper src="/car-icon.svg" alt="car" width={18} height={18} cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL} />
                                }
                                error={errors.model ?? undefined}
                                clearable
                            />

                            <label className="block mb-4 w-full ">
                                <LocationMasterSearchInput
                                    value={lead.location || ""}
                                    onChange={(value, selectedLocation) => {
                                            const mapped = selectedLocation ? mapLocationToLeadFields(selectedLocation) : { locationData: {} };
                                            setLead((p) => ({ ...p, location: value, locationData: mapped.locationData || {} }));
                                            setErrors((p) => ({ ...p, location: null }));
                                        }}
                                />
                            </label>

                            <CustomInput
                                value={lead.name}
                                placeholder="Enter name"
                                leftIcon={<ImageWrapper src="/user.svg" alt="user" width={18} height={18} cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL} />}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                    setLead((p) => ({ ...p, name: val }));
                                    setErrors((p) => ({ ...p, name: validateName(val) || (val.trim() ? null : "Name is required") }));
                                }}
                                error={errors.name ?? undefined}
                            />

                            <CustomInput
                                type="tel"
                                value={lead.phone}
                                placeholder="Enter mobile number"
                                maxLength={10}
                                leftIcon={<ImageWrapper src="/phone.svg" alt="phone" width={18} height={18} cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL} />}
                                onChange={(e) => {
                                    const val = sanitizePhoneInput(e.target.value).slice(0, 10);
                                    setLead((p) => ({ ...p, phone: val }));
                                    setErrors((p) => ({ ...p, phone: validatePhoneNumber(val) }));
                                }}
                                error={errors.phone ?? undefined}
                            />

                            {emailRequired && (
                                <CustomInput
                                    type="email"
                                    value={lead.email || ""}
                                    placeholder="Enter email address"
                                    maxLength={254}
                                    leftIcon={
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#90a1b9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <rect x="2" y="4" width="20" height="16" rx="2" />
                                            <path d="m22 7-10 6L2 7" />
                                        </svg>
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\s/g, "").slice(0, 254);
                                        setLead((p) => ({ ...p, email: val }));
                                        setErrors((p) => ({ ...p, email: validateEmail(val) }));
                                    }}
                                    error={errors.email ?? undefined}
                                />
                            )}

                            <LeadConsent
                                consent={consent}
                            />

                            <button
                                onClick={handleSubmit}
                                disabled={isDisabled}
                                className={`w-full py-3.5 rounded-lg font-medium text-white transition ${isDisabled
                                    ? "bg-teal-600/50 cursor-not-allowed"
                                    : "bg-teal-600 hover:bg-teal-700 active:bg-teal-800"
                                    } focus:outline-none focus:ring-2 focus:ring-teal-500/50`}
                            >
                                {isLoading ? "Submitting..." : "Submit"}
                            </button>
                            </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            )}
            <ThanksPopup
                isOpen={showThanks}
                onClose={closeThanksPopup}
                modelId={thanksModelId}
                leadData={lastLeadData}
                model={thanksModel}
            />
            <Notification
                message={notification.msg}
                isVisible={notification.show}
                isError={notification.error}
            />
        </>,
        document.body
    );
};

export default BrandIntentLeadForm;